/**
 * Utility functions for network error detection and handling
 */

/**
 * Checks if an error is likely a network/connectivity error that indicates
 * the server might be down or unreachable
 */
export function isNetworkError(error: Error): boolean {
  const message = error.message?.toLowerCase() || "";
  const name = error.name?.toLowerCase() || "";

  // Common network error patterns
  return (
    // Fetch API errors
    message.includes("fetch") ||
    message.includes("network") ||
    message.includes("failed to fetch") ||
    // Connection errors
    message.includes("connection") ||
    message.includes("timeout") ||
    message.includes("refused") ||
    message.includes("reset") ||
    // CORS errors (often indicates server issues)
    message.includes("cors") ||
    message.includes("cross-origin") ||
    // DNS/Host errors
    message.includes("getaddrinfo") ||
    message.includes("enotfound") ||
    message.includes("econnrefused") ||
    // Generic network error types
    name === "typeerror" ||
    name === "networkerror" ||
    // Specific error codes
    message.includes("err_network") ||
    message.includes("err_internet_disconnected") ||
    message.includes("err_connection_refused")
  );
}

/**
 * Constant for network error retry count
 */
export const NETWORK_ERROR_RETRY_COUNT = 0; // Don't retry network errors - let server health monitor handle it

/**
 * The retry count for general (non-network) errors.
 */
export const GENERAL_ERROR_RETRY_COUNT = 1; // Retry other errors once

/**
 * Extracts HTTP status and a human message from various error shapes
 * that can come from openapi-fetch/openapi-react-query or Fetch API.
 */
export function getHttpStatusAndMessage(
  err:
    | {
        status?: number;
        response?: { status?: number };
        data?: { message?: string };
        message?: string;
      }
    | Error
    | string
    | null
    | undefined
): { status?: number; message?: string } {
  if (typeof err === "string") return { message: err };

  if (err && typeof err === "object") {
    const e = err as {
      status?: number;
      response?: { status?: number };
      data?: { message?: string };
      message?: string;
    };
    return {
      status: e.status ?? e.response?.status,
      message: e.data?.message ?? e.message,
    };
  }
  return {};
}
